import { useState, useEffect, useCallback } from "react"
import { useFormik } from "formik"
import { useNavigate } from "react-router-dom"

import { useUI } from "providers/UIProvider"

import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemToLocalStorage,
} from "utils/localStorage"
import useCheckAuthorizeByPhoneMutation from "api/mutations/auth/useCheckAuthorizeByPhone"
import useAuthorizeByOtpMutation from "api/mutations/auth/useAuthorizeByOtp"
import { OTP_COUNTDOWN_DURATION_SECONDS } from "utils/constants"

import * as Yup from "yup"
import useCountdown from "./useCountdown"
import { useAppContext } from "providers/AppProvider/AppProvider"
import { toast } from "react-toastify"
import { fetchToken } from "../firebase"
import authService from "api/authService"
const phoneRegExp = /^5[0-9]{8}$/

const AUTH_FORM_INITIAL_VALUES = {
  phone: "",
}

const LOGIN_SCHEMA = (t: any) =>
  Yup.object({
    phone: Yup.string().matches(phoneRegExp, t("auth.form.validations.phone")),
  })

interface OtpState {
  sessionId: string
  OTPValue: string
  errorMessage: string
  invalidAttempts: number
}

const OtpDefaultState = {
  sessionId: "",
  OTPValue: "",
  errorMessage: "",
  invalidAttempts: 0,
}

const useAuth = () => {
  const { T } = useUI()
  const navigate = useNavigate()
  const { setState } = useAppContext()
  const token = getItemFromLocalStorage("token")

  const { startCountdown, stopCountdown, countdown, minutes, seconds } =
    useCountdown(OTP_COUNTDOWN_DURATION_SECONDS)

  const [{ sessionId, OTPValue, errorMessage, invalidAttempts }, setOtp] =
    useState<OtpState>(OtpDefaultState)

  const [fcmToken, setFcmToken] = useState<string>("")

  const {
    mutate,
    data,
    error,
    isLoading: checkAuthorizeByPhoneLoading,
  } = useCheckAuthorizeByPhoneMutation()
  const {
    mutate: authorizeByOtp,
    data: authorizeByOtpData,
    error: authorizeByOtpError,
    isLoading: authorizeByOtpLoading,
  } = useAuthorizeByOtpMutation()

  const setOtpValue = (key: string, value: string | number) =>
    setOtp((currentOtp) => ({ ...currentOtp, [key]: value }))

  const resetOtpValue = () => setOtp(OtpDefaultState)

  const handleSubmit = async () => {
    const isValid = values.phone && Object.values(errors).length === 0
    if (isValid && !sessionId) {
      mutate({ phone: String(values.phone) })
    }
  }

  const handleLoginClick = async () => {
    if (OTPValue.length === 4 && sessionId) {
      authorizeByOtp({
        fcmToken,
        data: { sessionId: sessionId, otp: String(OTPValue) },
      })
    }
  }

  const handleLogoutClick = () => {
    authService
      .removeFireBaseToken(fcmToken, token)
      .then((data) => {
        // Handle success, if needed
        console.log("Firebase token removed successfully:", data)
      })
      .then(() => {
        window.location.href = "/dashboard" //to fix caching bug
      })
      .catch((error) => {
        // Handle errors, if needed
        console.error("Error removing Firebase token:", error)
      })
    removeItemFromLocalStorage("token")
    removeItemFromLocalStorage("role")
    setState({
      user: null,
      userRole: null,
      isAuthenticated: false,
      isAdmin: false,
    })
    // navigate("/")
  }

  const handleOTPChange = (value: string) => {
    setOtpValue("OTPValue", value)
    if (errorMessage) {
      setOtpValue("errorMessage", "")
    }
  }

  const { values, errors, touched, handleBlur, handleChange, setErrors } =
    useFormik({
      initialValues: AUTH_FORM_INITIAL_VALUES,
      validationSchema: () => LOGIN_SCHEMA(T),
      onSubmit: () => handleSubmit(),
    })

  const fetchAndSetFCMToken = useCallback(async () => {
    const firebaseToken = await fetchToken()
    setFcmToken(firebaseToken)
  }, [])

  useEffect(() => {
    if (authorizeByOtpData) {
      const { accessToken, err, errStatus, roles } = authorizeByOtpData

      if (accessToken) {
        stopCountdown()
        setOtpValue("sessionId", "")
        setItemToLocalStorage("token", accessToken)
        setItemToLocalStorage("role", roles[0])
        setState({ isAuthenticated: true })
        navigate("/dashboard")
      }

      if (err && errStatus !== 403) {
        toast.error(T("auth.form.validations.otp_invalid"), {})
      }

      if (err && errStatus === 403) {
        setOtpValue("errorMessage", T("auth.form.validations.invalid_opt"))
        setOtpValue("invalidAttempts", Number(invalidAttempts + 1) as number)

        if (invalidAttempts === 2) {
          resetOtpValue()
          stopCountdown()
          setErrors({
            ...errors,
            phone: T("auth.form.validations.otp_invalid_attempts"),
          })

          setTimeout(() => {
            setErrors({
              ...errors,
              phone: "",
            })
          }, 3000)
        }
      }
    }
  }, [authorizeByOtpData])

  useEffect(() => {
    if (data) {
      const { sessionId, userExists } = data
      if (!userExists) {
        setErrors({ phone: T("auth.form.user_not_exists") })
      }

      if (userExists && sessionId) {
        setOtpValue("sessionId", sessionId)
        startCountdown()
      }
    }
  }, [data])

  useEffect(() => {
    if (error) {
      setErrors({ phone: T("general.network_error") })
    }

    if (authorizeByOtpError) {
      setErrors({ phone: T("general.network_error") })
    }
  }, [error, authorizeByOtpError])

  useEffect(() => {
    if (countdown === 0) {
      resetOtpValue()
    }
  }, [countdown])

  useEffect(() => {
    fetchAndSetFCMToken()
  }, [])

  return {
    otp: {
      sessionId,
      OTPValue,
      errorMessage,
      minutes,
      seconds,
    },
    handleOTPChange,
    handleLoginClick,
    handleLogoutClick,
    loading: {
      checkAuthorizeByPhoneLoading,
      authorizeByOtpLoading,
    },
    form: {
      values,
      errors,
      touched,
      handleBlur,
      handleChange,
      handleSubmit,
    },
  }
}

export default useAuth
